import { add, type Duration } from "date-fns";

/**
 * Parses an ISO 8601 duration string into a date-fns Duration object.
 * Supports: P1Y, P6M, P30D, P1Y6M, P1Y6M15D, P2W, etc.
 * Does not support time components (hours, minutes, seconds).
 * @throws Error if the duration string is invalid or contains time components.
 */
export function parseIsoDuration(durationStr: string): Duration {
  // Reject time components (T followed by H, M, or S)
  if (/T/.test(durationStr)) {
    throw new Error(
      `Invalid ISO 8601 duration: time components (T, H, M, S) are not supported. Got: "${durationStr}"`
    );
  }

  // Full-match regex for date-only ISO 8601 duration: P[nY][nM][nW][nD]
  // At least one component must be present after P
  const fullMatch = durationStr.match(
    /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?$/
  );

  if (!fullMatch) {
    throw new Error(
      `Invalid ISO 8601 duration format. Expected P[nY][nM][nW][nD] (e.g., P1Y, P6M, P1Y6M15D). Got: "${durationStr}"`
    );
  }

  const [, years, months, weeks, days] = fullMatch;

  // Ensure at least one component is present (regex allows "P" alone)
  if (!years && !months && !weeks && !days) {
    throw new Error(
      `Invalid ISO 8601 duration: at least one component (Y, M, W, D) is required. Got: "${durationStr}"`
    );
  }

  const duration: Duration = {};

  if (years) duration.years = parseInt(years, 10);
  if (months) duration.months = parseInt(months, 10);
  if (weeks) duration.weeks = parseInt(weeks, 10);
  if (days) duration.days = parseInt(days, 10);

  return duration;
}

/**
 * Parse ISO 8601 duration string and add to date
 */
export function addDurationToDate(date: Date, duration: string): Date {
  const parsedDuration = parseIsoDuration(duration);
  return add(date, parsedDuration);
}
